import type { Country } from "@/i18n/config"

/**
 * The base blood check-up a market sells, as a `{ slug, labelKey }` pair.
 *
 * Germany replaced the Advanced blood test with the entry-level
 * `prevention-starter` ("Vorsorge-Starter", €199, 67 markers): the DE market
 * hides `advanced-blood-test` (`{ "de-de": false }`, 308-redirected) and sells
 * `prevention-starter` instead. Every other market keeps the Advanced blood
 * test. This is the single source of truth for "which blood product does this
 * market link to and label", so no surface hardcodes `country === "de"` to pick
 * between the two — nav, footer, homepage cards, the DE city LPs, the quiz,
 * search presets and the markdown mirrors all resolve through here.
 *
 * `labelKey` is a `nav`-namespace translation key (both labels live there):
 * `preventionStarter` → "Vorsorge-Starter" on DE, `advancedBloodTest` →
 * "Advanced blood test" elsewhere.
 */
export interface BaseBloodTest {
  /** `/tests/<slug>` product page for this market's base blood check-up. */
  slug: string
  /** `nav`-namespace label key for the product's name. */
  labelKey: "preventionStarter" | "advancedBloodTest"
  /**
   * CMS `packages` tag of the biomarkers this product measures, for
   * `BiomarkerDiseaseExplorer`. On DE this is the same tag the copy's
   * `%panelCount%` is counted from, so the list and the number cannot drift.
   */
  biomarkerPackage: "prevention-starter" | "blood"
}

const PREVENTION_STARTER: BaseBloodTest = {
  slug: "prevention-starter",
  labelKey: "preventionStarter",
  biomarkerPackage: "prevention-starter",
}

const ADVANCED_BLOOD_TEST: BaseBloodTest = {
  slug: "advanced-blood-test",
  labelKey: "advancedBloodTest",
  biomarkerPackage: "blood",
}

/** The base blood check-up product for `country`. */
export function baseBloodTest(country: Country | string): BaseBloodTest {
  return country === "de" ? PREVENTION_STARTER : ADVANCED_BLOOD_TEST
}
